// Importing the API base URL and Job type
import { BUSINESS_API_URL } from '../config/constants';
import { Job } from '../types/job';

/**
 * Fetches all jobs from the API.
 * @param token - Optional authorization token for authenticated requests.
 * @returns A promise resolving to an array of jobs.
 */
export async function fetchJobs(token?: string) {
  try {
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`; // Attach token if provided
    }

    const response = await fetch(`${BUSINESS_API_URL}/job`, { headers });

    if (!response.ok) {
      throw new Error('Failed to fetch jobs'); // Handle non-successful responses
    }

    return response.json(); // Parse and return JSON response
  } catch (error) {
    console.error('Fetch jobs error:', error);
    throw error; // Re-throw error for caller to handle
  }
}

/**
 * Creates a new job posting.
 * @param token - Authorization token for the request.
 * @param jobData - Partial job data to create the job.
 * @returns A promise resolving to the newly created job.
 */
export async function createJob(token: string, jobData: Partial<Job>) {
  try {
    const response = await fetch(`${BUSINESS_API_URL}/job`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json', // Ensure JSON body is processed correctly
        'Authorization': `Bearer ${token}`, // Authorization token
      },
      body: JSON.stringify(jobData), // Job data payload
    });

    if (!response.ok) {
      throw new Error('Failed to create job');
    }

    return response.json(); // Parse and return created job data
  } catch (error) {
    console.error('Create job error:', error);
    throw error;
  }
}

/**
 * Updates an existing job posting.
 * @param token - Authorization token for the request.
 * @param id - ID of the job to update.
 * @param jobData - Partial job data to update the job.
 * @returns A promise resolving to the updated job.
 */
export async function updateJob(token: string, id: number, jobData: Partial<Job>) {
  try {
    const response = await fetch(`${BUSINESS_API_URL}/job/${id}`, {
      method: 'PUT', // HTTP PUT method for updates
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(jobData),
    });

    if (!response.ok) {
      throw new Error('Failed to update job');
    }

    return response.json();
  } catch (error) {
    console.error('Update job error:', error);
    throw error;
  }
}

/**
 * Deletes a job posting by ID.
 * @param token - Authorization token for the request.
 * @param id - ID of the job to delete.
 * @returns A promise resolving to `true` if the job was successfully deleted.
 */
export async function deleteJob(token: string, id: number) {
  try {
    const response = await fetch(`${BUSINESS_API_URL}/job/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete job');
    }

    return true; // Return `true` to indicate success
  } catch (error) {
    console.error('Delete job error:', error);
    throw error;
  }
}

/**
 * Toggles the promotion status of a job.
 * @param token - Authorization token for the request.
 * @param job - The job object whose promotion status will be toggled.
 * @returns A promise resolving to the updated job.
 */
export async function toggleJobPromotion(token: string, job: Job) {
  try {
    const response = await fetch(`${BUSINESS_API_URL}/job/${job.id}`, {
      method: 'PATCH', // HTTP PATCH method for partial updates
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...job, // Spread the existing job data
        promoted: !job.promoted, // Toggle the promotion status
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to toggle job promotion');
    }

    return response.json();
  } catch (error) {
    console.error('Toggle job promotion error:', error);
    throw error;
  }
}

/**
 * Fetches a specific job by its ID.
 * @param id - ID of the job to fetch.
 * @param token - Optional authorization token for authenticated requests.
 * @returns A promise resolving to the job details.
 */
export async function getJobById(id: number, token?: string) {
  try {
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${BUSINESS_API_URL}/job/${id}`, { headers });

    if (!response.ok) {
      throw new Error('Failed to fetch job details');
    }

    return response.json();
  } catch (error) {
    console.error('Get job by id error:', error);
    throw error;
  }
}
